import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Video from 'twilio-video';
import { useAppointment } from '../context/appointmentContext';
import { client } from '../helpers/api-client';
import {
  acquireAudioTrack,
  acquireLocalTracks,
  acquireVideoTrack,
  buildAudioConstraints,
  buildVideoConstraints,
  connectErrorInfo,
  deriveStatus,
  mediaStateFromResult,
  MEDIA_ERROR_STATES,
  VIDEO_CONSTRAINT_FALLBACKS,
} from '../helpers/video/media';

const VideoContext = createContext();

export const useVideo = () => {
  return useContext(VideoContext);
};

const errorInfo = (error) => (error ? { name: error.name || 'Error', message: error.message || String(error) } : null);
const trackList = (tracks) => [tracks.video, tracks.audio].filter(Boolean);
const NO_TRACKS = { video: null, audio: null };
// 'granted' | 'prompt' | 'denied' | null (unsupported, e.g. Safari < 16 / Firefox for camera)
const queryCameraPermission = async () => {
  try {
    return (await navigator.permissions?.query({ name: 'camera' }))?.state || null;
  } catch {
    return null;
  }
};
const applyEnabled = (tracks, enabled) => {
  if (tracks.video && !enabled.video) tracks.video.disable();
  if (tracks.audio && !enabled.audio) tracks.audio.disable();
};

// Owns the local camera/mic tracks and the Twilio room.
//
// Local media is acquired independently of the room (so a preview shows even if Twilio
// is unreachable, and a blocked camera does not stop the clinician/patient from seeing
// each other), and the room connection does not wait for the permission prompt: tracks
// acquired later are published into the room by syncPublishedTracks().
//
// State:
//   mediaStatus:      idle | requesting-permission | permission-denied | no-device | device-in-use | ready | failed
//   connectionStatus: idle | connecting | connected | reconnecting | disconnected | failed
//   status:           the two collapsed into one (see deriveStatus)
export const VideoProvider = ({ children }) => {
  const { appointment } = useAppointment();
  const [room, setRoom] = useState(false);
  const [participants, setParticipants] = useState(null);
  const [localTracks, setLocalTracks] = useState(NO_TRACKS);
  const [media, setMedia] = useState({ status: 'idle', error: null, audioError: null, deviceError: null, publishError: null, permissionState: null });
  const [connection, setConnection] = useState({ status: 'idle', error: null });
  const [isCameraEnabled, setCameraEnabled] = useState(true);
  const [isMicEnabled, setMicEnabled] = useState(true);
  const [devices, setDevices] = useState({ videoInputs: [], audioInputs: [] });
  const [selectedDevices, setSelectedDevices] = useState({ video: null, audio: null });

  const roomRef = useRef(null);
  const tracksRef = useRef(NO_TRACKS);
  const selectedRef = useRef({ video: null, audio: null });
  const enabledRef = useRef({ video: true, audio: true });
  const mediaPromiseRef = useRef(null);
  const connectingRef = useRef(false);
  const leftRef = useRef(false); // user left the session: no automatic reconnects
  const reconnectRef = useRef(false); // next 'disconnected' should go back to idle and reconnect
  const restartingRef = useRef(false);
  const pageHiddenRef = useRef(false); // page is unloading or in the bfcache
  const unmountedRef = useRef(false);
  const initialTokenRef = useRef(null);
  const initialTokenUsedRef = useRef(false);

  const isOpen = appointment?.currentAppointment?.status === 'open';
  const roomName = appointment?.currentAppointment?.room_name;
  const roomNameRef = useRef(roomName);
  roomNameRef.current = roomName;
  if (!initialTokenUsedRef.current && appointment?.appointmentToken) initialTokenRef.current = appointment.appointmentToken;

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      if (unmountedRef.current) return;
      const pick = (kind) => list.filter((d) => d.kind === kind && d.deviceId).map((d, i) => ({ deviceId: d.deviceId, label: d.label || `${kind === 'videoinput' ? 'Camera' : 'Microphone'} ${i + 1}` }));
      setDevices({ videoInputs: pick('videoinput'), audioInputs: pick('audioinput') });
    } catch (e) {
      console.warn('enumerateDevices failed', e);
    }
  }, []);

  // Make the room's published tracks match tracksRef (publish new ones, unpublish replaced ones).
  const syncPublishedTracks = useCallback(() => {
    const twilioRoom = roomRef.current;
    if (!twilioRoom || twilioRoom.state === 'disconnected') return;
    const lp = twilioRoom.localParticipant;
    const wanted = trackList(tracksRef.current);
    const published = Array.from(lp.tracks.values()).map((pub) => pub.track);
    published.filter((t) => t && !wanted.includes(t)).forEach((t) => lp.unpublishTrack(t));
    wanted
      .filter((t) => !published.includes(t))
      .forEach((t) =>
        lp.publishTrack(t).catch((error) => {
          console.error('Failed to publish local track', error);
          setMedia((m) => ({ ...m, publishError: errorInfo(error) }));
        }),
      );
  }, []);

  const onTrackStopped = useCallback((track) => {
    // Camera unplugged, taken by the OS or another app, or iOS backgrounding. Our own stop()
    // calls detach this listener first; restart() is flagged.
    if (restartingRef.current || tracksRef.current.video !== track) return;
    setMedia((m) => ({ ...m, status: 'failed', error: { name: 'TrackEnded', message: 'The camera stopped. It may have been disconnected or taken by another application.' } }));
  }, []);

  const onTrackStarted = useCallback((track) => {
    if (tracksRef.current.video !== track) return;
    setMedia((m) => (m.error?.name === 'TrackEnded' ? { ...m, status: 'ready', error: null } : m));
  }, []);

  const setTracks = useCallback(
    (next) => {
      const prev = tracksRef.current;
      tracksRef.current = next;
      [prev.video].filter((t) => t && t !== next.video).forEach((t) => {
        t.off('stopped', onTrackStopped);
        t.off('started', onTrackStarted);
      });
      if (next.video && next.video !== prev.video) {
        next.video.on('stopped', onTrackStopped);
        next.video.on('started', onTrackStarted);
      }
      setLocalTracks(next);
      syncPublishedTracks();
      trackList(prev)
        .filter((t) => !trackList(next).includes(t))
        .forEach((t) => t.stop());
    },
    [onTrackStopped, onTrackStarted, syncPublishedTracks],
  );

  const startMedia = useCallback(() => {
    if (mediaPromiseRef.current) return mediaPromiseRef.current;
    const run = async () => {
      setMedia((m) => ({ ...m, status: 'requesting-permission', error: null, audioError: null, deviceError: null }));
      const permissionState = await queryCameraPermission();
      setMedia((m) => ({ ...m, permissionState }));
      const result = await acquireLocalTracks(Video.createLocalTracks, {
        videoDeviceId: selectedRef.current.video,
        audioDeviceId: selectedRef.current.audio,
      });
      const next = { video: result.video || null, audio: result.audio || null };
      if (unmountedRef.current || leftRef.current) {
        trackList(next).forEach((t) => t.stop());
        return tracksRef.current;
      }
      applyEnabled(next, enabledRef.current);
      setTracks(next);
      setMedia((m) => ({ ...m, ...mediaStateFromResult(result), publishError: null }));
      refreshDevices();
      return next;
    };
    mediaPromiseRef.current = run().finally(() => {
      mediaPromiseRef.current = null;
    });
    return mediaPromiseRef.current;
  }, [setTracks, refreshDevices]);

  // Release the devices before asking again: a busy camera is often busy because of us.
  const retryMedia = useCallback(() => {
    setTracks(NO_TRACKS);
    return startMedia();
  }, [setTracks, startMedia]);

  const attachRoom = useCallback(
    (twilioRoom) => {
      roomRef.current = twilioRoom;
      const remote = () => Array.from(twilioRoom.participants.values());
      twilioRoom.on('participantConnected', (participant) => setParticipants(participant));
      twilioRoom.on('participantDisconnected', (participant) =>
        setParticipants((prev) => {
          const others = remote().filter((p) => p !== participant);
          if (prev && prev !== participant && others.includes(prev)) return prev;
          return others[others.length - 1] || null;
        }),
      );
      twilioRoom.on('reconnecting', (error) => setConnection({ status: 'reconnecting', error: connectErrorInfo(error) }));
      twilioRoom.on('reconnected', () => setConnection({ status: 'connected', error: null }));
      twilioRoom.localParticipant.on('trackPublicationFailed', (error) => setMedia((m) => ({ ...m, publishError: errorInfo(error) })));
      twilioRoom.localParticipant.on('trackPublished', () => setMedia((m) => (m.publishError ? { ...m, publishError: null } : m)));
      twilioRoom.on('disconnected', (_room, error) => {
        if (roomRef.current !== twilioRoom) return;
        roomRef.current = null;
        setRoom(false);
        setParticipants(null);
        if (leftRef.current || pageHiddenRef.current) {
          setConnection({ status: 'disconnected', error: null });
        } else if (reconnectRef.current) {
          reconnectRef.current = false;
          setConnection({ status: 'idle', error: null });
        } else {
          setConnection({ status: error ? 'failed' : 'disconnected', error: connectErrorInfo(error) });
        }
      });
      setParticipants(remote()[0] || null);
      setRoom(twilioRoom);
      setConnection({ status: 'connected', error: null });
      syncPublishedTracks();
    },
    [syncPublishedTracks],
  );

  const connect = useCallback(async () => {
    if (connectingRef.current || roomRef.current) return;
    connectingRef.current = true;
    setConnection({ status: 'connecting', error: null });
    try {
      if (!Video.isSupported) {
        const error = new Error('This browser does not support video calls. Please use an up-to-date Chrome, Safari, Edge or Firefox.');
        error.name = 'UnsupportedError';
        throw error;
      }
      let token = initialTokenUsedRef.current ? null : initialTokenRef.current;
      initialTokenUsedRef.current = true;
      // Tokens expire; every attempt after the first gets a fresh one.
      if (!token) token = await client(`api/appointments/${roomNameRef.current}/appointment_token/`);
      const twilioRoom = await Video.connect(token.twilio_token, {
        name: token.room_name,
        tracks: trackList(tracksRef.current),
      });
      if (unmountedRef.current || leftRef.current) {
        twilioRoom.disconnect();
        return;
      }
      attachRoom(twilioRoom);
    } catch (error) {
      console.error('Video connect failed', error);
      if (!unmountedRef.current) setConnection({ status: 'failed', error: connectErrorInfo(error) });
    } finally {
      connectingRef.current = false;
    }
  }, [attachRoom]);

  // Acquire local media as soon as the session is known to be open.
  useEffect(() => {
    if (isOpen && media.status === 'idle' && !leftRef.current && !pageHiddenRef.current) startMedia();
  }, [isOpen, media.status, startMedia]);

  // Connect (without waiting for the permission prompt) and reconnect after a refresh.
  useEffect(() => {
    if (isOpen && connection.status === 'idle' && !roomRef.current && !leftRef.current && !pageHiddenRef.current) connect();
  }, [isOpen, connection.status, connect]);

  // When the user unblocks the camera in site settings, retry automatically (Chrome/Edge/Firefox).
  useEffect(() => {
    if (media.status !== 'permission-denied' || !navigator.permissions?.query) return undefined;
    let permission;
    let cancelled = false;
    navigator.permissions
      .query({ name: 'camera' })
      .then((p) => {
        if (cancelled) return;
        permission = p;
        permission.onchange = () => {
          if (permission.state !== 'denied') retryMedia();
        };
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (permission) permission.onchange = null;
    };
  }, [media.status, retryMedia]);

  useEffect(() => {
    if (!navigator.mediaDevices?.addEventListener) return undefined;
    const onDeviceChange = () => refreshDevices();
    navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    return () => navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
  }, [refreshDevices]);

  // Leave the room promptly when the tab closes so the other side sees it at once.
  // (pagehide rather than beforeunload: beforeunload also fires for downloads/mailto links.)
  // On a bfcache restore (pageshow) the effects above reconnect.
  useEffect(() => {
    const onPageHide = () => {
      pageHiddenRef.current = true;
      roomRef.current?.disconnect();
      trackList(tracksRef.current).forEach((t) => t.stop());
    };
    const onPageShow = (event) => {
      if (!event.persisted || !pageHiddenRef.current) return;
      pageHiddenRef.current = false;
      if (leftRef.current) return;
      tracksRef.current = NO_TRACKS;
      setLocalTracks(NO_TRACKS);
      setMedia((m) => ({ ...m, status: 'idle' }));
      setConnection({ status: 'idle', error: null });
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      roomRef.current?.disconnect();
      roomRef.current = null;
      trackList(tracksRef.current).forEach((t) => t.stop());
      tracksRef.current = NO_TRACKS;
    };
  }, []);

  // Leave the session for good (end session / logout / leave). Stops the camera.
  const disconnect = useCallback(() => {
    leftRef.current = true;
    roomRef.current?.disconnect();
    setTracks(NO_TRACKS);
    setMedia((m) => ({ ...m, status: 'idle', error: null }));
    setConnection({ status: 'disconnected', error: null });
  }, [setTracks]);

  // The "refresh video" buttons: drop and re-establish the room, re-acquiring media if it is broken.
  const reconnect = useCallback(() => {
    leftRef.current = false;
    pageHiddenRef.current = false;
    if (MEDIA_ERROR_STATES.includes(media.status) || !tracksRef.current.video) retryMedia();
    if (roomRef.current) {
      reconnectRef.current = true;
      roomRef.current.disconnect();
    } else if (!connectingRef.current) {
      setConnection({ status: 'idle', error: null });
    }
  }, [media.status, retryMedia]);

  // Fix whatever is broken: media, connection, or both.
  const retry = useCallback(() => {
    leftRef.current = false;
    pageHiddenRef.current = false;
    if (MEDIA_ERROR_STATES.includes(media.status) || media.status === 'idle' || !tracksRef.current.video) retryMedia();
    if (!roomRef.current && !connectingRef.current && connection.status !== 'connecting') setConnection({ status: 'idle', error: null });
  }, [media.status, connection.status, retryMedia]);

  const toggleCamera = useCallback(() => {
    const track = tracksRef.current.video;
    if (!track) return retryMedia();
    if (track.isEnabled) track.disable();
    else track.enable();
    enabledRef.current.video = track.isEnabled;
    setCameraEnabled(track.isEnabled);
  }, [retryMedia]);

  const toggleMic = useCallback(async () => {
    const track = tracksRef.current.audio;
    if (!track) {
      const { track: audio, error } = await acquireAudioTrack(Video.createLocalTracks, { deviceId: selectedRef.current.audio });
      if (audio) {
        enabledRef.current.audio = true;
        setMicEnabled(true);
        setTracks({ ...tracksRef.current, audio });
      }
      setMedia((m) => ({ ...m, audioError: errorInfo(error) }));
      return;
    }
    if (track.isEnabled) track.disable();
    else track.enable();
    enabledRef.current.audio = track.isEnabled;
    setMicEnabled(track.isEnabled);
  }, [setTracks]);

  // Switch camera/microphone. restart() keeps the same track object, so the room publication
  // and every attached <video> element carry on without re-publishing.
  const switchDevice = useCallback(
    async (kind, deviceId) => {
      selectedRef.current = { ...selectedRef.current, [kind]: deviceId || null };
      setSelectedDevices({ ...selectedRef.current });
      setMedia((m) => ({ ...m, deviceError: null }));
      const track = tracksRef.current[kind];
      try {
        if (!track) {
          const acquired =
            kind === 'video'
              ? await acquireVideoTrack(Video.createLocalTracks, { deviceId })
              : await acquireAudioTrack(Video.createLocalTracks, { deviceId });
          if (acquired.error) throw acquired.error;
          applyEnabled({ [kind]: acquired.track }, enabledRef.current);
          setTracks({ ...tracksRef.current, [kind]: acquired.track });
          if (kind === 'video') setMedia((m) => ({ ...m, status: 'ready', error: null }));
          else setMedia((m) => ({ ...m, audioError: null }));
          return;
        }
        restartingRef.current = true;
        if (kind === 'audio') {
          await track.restart(buildAudioConstraints(deviceId));
        } else {
          let lastError;
          for (const base of VIDEO_CONSTRAINT_FALLBACKS) {
            try {
              await track.restart(buildVideoConstraints(base, deviceId));
              lastError = null;
              break;
            } catch (error) {
              lastError = error;
            }
          }
          if (lastError) throw lastError;
          setMedia((m) => (m.error?.name === 'TrackEnded' ? { ...m, status: 'ready', error: null } : m));
        }
      } catch (error) {
        console.error(`Switching ${kind} device failed`, error);
        setMedia((m) => ({ ...m, deviceError: errorInfo(error) }));
      } finally {
        restartingRef.current = false;
      }
    },
    [setTracks],
  );

  return (
    <VideoContext.Provider
      value={{
        room,
        participants,
        disconnect,
        reconnect,
        retry,
        retryMedia,
        localTracks,
        mediaStatus: media.status,
        permissionState: media.permissionState,
        mediaError: media.error,
        audioError: media.audioError,
        deviceError: media.deviceError,
        publishError: media.publishError,
        connectionStatus: connection.status,
        connectionError: connection.error,
        status: deriveStatus(media.status, connection.status),
        isCameraEnabled,
        isMicEnabled,
        toggleCamera,
        toggleMic,
        devices,
        selectedDevices,
        switchDevice,
      }}
    >
      {children}
    </VideoContext.Provider>
  );
};
